import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { BillingService } from '@galaxy/billing';
import { App, AppPrices, AppPricingService, FieldMask, UnitBillingFrequency } from '@galaxy/marketplace-apps';
import { TranslateModule } from '@ngx-translate/core';
import { RuleInterface } from '@vendasta/automata';
import { BillingUiModule } from '@vendasta/billing-ui';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { SelectInputOptionInterface } from '@vendasta/galaxy/input';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { RevenuePeriod } from '@vendasta/sales-orders';
import { BehaviorSubject, Observable, Subject, combineLatest, of } from 'rxjs';
import { map, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { AutomationViewService } from '../../../../../automation/automation-view.service';
import { AppsService } from '../../../../../data-services/apps.service';
import { CommonActionPanelComponent } from '../../../common/common-action-panel-component.directive';

@Component({
  selector: 'automata-activate-product-panel',
  templateUrl: './activate-product-panel.component.html',
  styleUrls: ['./activate-product-panel.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    GalaxyTooltipModule,
    BillingUiModule,
  ],
})
export class ActivateProductPanelComponent extends CommonActionPanelComponent implements OnInit, OnDestroy {
  apps$: Observable<App[]>;
  productControl: UntypedFormControl = new UntypedFormControl(null);

  disableLoadingProductSelection$ = this.automationService.isNonPartnerAutomation().pipe(
    take(1),
    map((isNonPartnerAutomation) => {
      if (isNonPartnerAutomation) {
        this.productControl.disable();
        return true;
      }
      this.productControl.setValidators(Validators.required);
      return false;
    }),
  );

  appOptions$: Observable<SelectInputOptionInterface[]>;

  private appPrices$$: BehaviorSubject<AppPrices[]> = new BehaviorSubject<AppPrices[]>([]);
  appPrices$: Observable<AppPrices[]> = this.appPrices$$.asObservable();
  partnerCurrency$: Observable<string>;
  pricingType = 'wholesale';

  destroyed$$ = new Subject<void>();

  constructor(
    protected readonly automationService: AutomationViewService,
    protected readonly appsService: AppsService,
    private readonly ref: ChangeDetectorRef,
    protected readonly appPricingService: AppPricingService,
    protected readonly billingService: BillingService,
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.apps$ = this.disableLoadingProductSelection$.pipe(
      switchMap((disableProductSelection) => {
        if (disableProductSelection) {
          return of([]);
        }
        return this.appsService.getLBOTApps();
      }),
      take(1),
      shareReplay(1),
    );

    const skusWithPricing$: Observable<string[]> = this.apps$.pipe(
      switchMap((apps) => {
        const appKeys = (apps || []).map((a) => this.appsService.appKeyToKeySku(a.key));
        return this.appsService.getMultiProductRetailPricing(appKeys);
      }),
      map((pricingDict) => {
        const skusWithPricing: string[] = [];
        for (const [sku, pricing] of Object.entries(pricingDict)) {
          if ((pricing?.retail?.pricingRules || []).length > 0) {
            skusWithPricing.push(sku);
          }
        }
        return skusWithPricing;
      }),
    );

    this.appOptions$ = combineLatest([this.apps$, skusWithPricing$]).pipe(
      map(([apps, skusWithPricing]) => {
        return apps.map((app) => {
          const appName = app.sharedMarketingInformation.name;
          const editionName = app.sharedMarketingInformation?.editionName
            ? ` | ${app.sharedMarketingInformation?.editionName}`
            : '';
          const sku = this.appsService.appKeyToKeySku(app.key);
          const disabled = !skusWithPricing.find((skuWithPricing) => sku === skuWithPricing);
          return {
            label: `${appName}${editionName}`,
            value: sku,
            disabled: disabled,
          } as SelectInputOptionInterface;
        });
      }),
      // Once the product and option lists are loaded we can let the user save the panel.
      tap(() => (this.savingEnabled = true)),
      take(1),
    );

    this.productControl.valueChanges
      .pipe(
        switchMap((appKeys) => {
          if (appKeys?.length > 0) {
            const appIds = appKeys.map((appKey) => this.appsService.skuToAppKey(appKey)).map((appKey) => appKey.appId);
            return this.appPricingService.getMultiPricing(
              appIds,
              new FieldMask({ paths: [this.pricingType] }),
              this.automation.namespace,
              this.automation.marketId,
            );
          }
          return of([]);
        }),
        tap((pricing) => this.appPrices$$.next(pricing)),
        takeUntil(this.destroyed$$),
      )
      .subscribe();

    this.partnerCurrency$ = this.billingService.getMultiContract([this.automation.namespace]).pipe(
      map((resp) => resp[this.automation.namespace]?.currency ?? 'USD'),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    const jsonData = this.step ? JSON.parse(this.step.data) : {};
    const lineItems = jsonData.line_items || [];
    this.productControl.setValue(
      lineItems.map((lineItem) =>
        this.appsService.appKeyToKeySku({
          appId: lineItem.identifier?.app_key?.app_id || '',
          editionId: lineItem.identifier?.app_key?.edition_id,
        }),
      ),
    );

    this.ref.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroyed$$.next();
  }

  getRules(): RuleInterface[] {
    return [];
  }

  getData(): any {
    const jsonData = this.step ? JSON.parse(this.step.data) : {};
    if (this.productControl.value?.length > 0) {
      jsonData.line_items = this.productControl.value.map((appKeyString) => {
        const appKey = this.appsService.skuToAppKey(appKeyString);
        const app_key = {
          app_id: appKey.appId,
          edition_id: appKey.editionId,
        };
        let pricing = null;

        this.appPrices$$.value.forEach((appPrice) => {
          if (appPrice.appId === appKey.appId) {
            pricing = appPrice.pricesForContexts[this.pricingType];
          }
        });

        if (!pricing) {
          return;
        }

        const currency = Object.keys(pricing.pricesForEditions[appKey.editionId].pricesForCurrencies)[0];
        const frequency =
          pricing.pricesForEditions[appKey.editionId].pricesForCurrencies[currency].pricesForFrequencies[0].frequency;
        return {
          identifier: {
            app_key,
          },
          quantity: 1,
          is_trial: false,
          currency_code: currency,
          current_revenue: {
            revenue_components: pricing.pricesForEditions[appKey.editionId].pricesForCurrencies[
              currency
            ].pricesForFrequencies[0].pricingRules.map((price) => ({
              value: price.price || 0,
              period: this.convertPricingFrequency(frequency),
              is_starting_revenue: false,
            })),
          },
        };
      });
    }
    return jsonData;
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    return [this.productControl];
  }

  isFormValid(): boolean {
    this.appPrices$$.value.forEach((appPrice) => {
      if (!appPrice.pricesForContexts[this.pricingType]) {
        this.productControl.setErrors({ 'invalid price': true });
      }
    });
    return !this.productControl.invalid;
  }

  private convertPricingFrequency(period: UnitBillingFrequency): RevenuePeriod {
    switch (period) {
      case UnitBillingFrequency.UNIT_BILLING_FREQUENCY_MONTHLY:
        return RevenuePeriod.MONTHLY;
      case UnitBillingFrequency.UNIT_BILLING_FREQUENCY_YEARLY:
        return RevenuePeriod.YEARLY;
      case UnitBillingFrequency.UNIT_BILLING_FREQUENCY_ONETIME:
        return RevenuePeriod.ONETIME;
      default:
        return RevenuePeriod.ONETIME;
    }
  }

  getProductNameFromString(appKeyString: string): Observable<string> {
    return this.appOptions$.pipe(
      map((appOption) => {
        const match = appOption.find((a) => a.value === appKeyString);
        return match?.label || appKeyString;
      }),
    );
  }

  getProductNameFromAppId(appId: string): Observable<string> {
    return this.appOptions$.pipe(
      map((appOption) => {
        const match = appOption.find((a) => this.appsService.skuToAppKey(a.value).appId === appId);

        return match?.label || appId;
      }),
    );
  }

  getProductEditionFromAppId(appId: string): Observable<string> {
    return this.appOptions$.pipe(
      map((appOption) => {
        const skus = appOption.map((ao) => ao.value);
        const appKeys = skus.map((s) => this.appsService.skuToAppKey(s));
        const match = appKeys.find((a) => a.appId === appId);

        return match.editionId;
      }),
    );
  }
}
